/**
 * Process the `fetch` response and throw a detailed error if the request failed.
 *
 * @param res - The Response object returned by `fetch`.
 * @returns The response body as plain text (when the request succeeds).
 * @throws An Error containing a human‑readable message extracted from the API payload.
 */
async function handleResponse(res: Response): Promise<string> {
  let responseText = '';

  // -------------------------------------------------------------------------
  // 1️⃣  Read the response body safely
  // -------------------------------------------------------------------------
  try {
    // `await` is now inside an async function → no more "top‑level await" error.
    responseText = await res.text();
  } catch (textError) {
    console.error('❌ Erro ao ler resposta:', textError);
    throw new Error(
      'Erro ao processar resposta da API. Por favor, tente novamente.'
    );
  }

  // -------------------------------------------------------------------------
  // 2️⃣  Debug logs (optional – keep or remove in production)
  // -------------------------------------------------------------------------
  console.log('📡 Status da resposta:', res.status);
  console.log('📡 Resposta completa:', responseText);

  // -------------------------------------------------------------------------
  // 3️⃣  If the request was not successful, build a helpful error message
  // -------------------------------------------------------------------------
  if (!res.ok) {
    let errorMessage = `Erro HTTP ${res.status}`;

    try {
      // Only attempt to parse JSON when we have a non‑empty body
      // Fixed: Check if responseText exists and is a string before calling trim()
      if (responseText && typeof responseText === 'string' && responseText.trim().length > 0) {
        const errorData = JSON.parse(responseText);
        console.error('❌ Erro da API:', errorData);

        // ----- Extract the most specific error description possible -----
        if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        } else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        } else if (Array.isArray(errorData.errors) && errorData.errors.length) {
          const errorMessages = errorData.errors
            .map((e: any) => {
              if (typeof e === 'string') return e;
              if (e?.message) return e.message;
              if (e?.title) return e.title;
              return JSON.stringify(e);
            })
            .filter((msg: string) => !!msg);

          errorMessage = errorMessages.length
            ? errorMessages.join('; ')
            : `Erro HTTP ${res.status}: ${JSON.stringify(errorData)}`;
        } else {
          errorMessage = `Erro HTTP ${res.status}: ${JSON.stringify(errorData)}`;
        }
      } else {
        // Empty body → fallback to status text
        errorMessage = `Erro HTTP ${res.status}: ${
          res.statusText || 'Resposta vazia'
        }`;
      }
    } catch (parseError) {
      // JSON parsing failed – show raw payload or status text
      console.error('❌ Erro ao fazer parse do erro:', parseError);
      const statusText = res.statusText || 'Erro desconhecido';
      errorMessage = `Erro HTTP ${res.status}: ${responseText || statusText}`;
    }

    // Throw the assembled error so callers can catch it
    throw new Error(errorMessage);
  }

  // -------------------------------------------------------------------------
  // 4️⃣  Successful request – return the raw text (caller can parse if needed)
  // -------------------------------------------------------------------------
  return responseText;
}

// -------------------------------------------------------------------------
// Types and Interfaces
// -------------------------------------------------------------------------

export interface DuffelOffer {
  id: string;
  total_amount: string;
  total_currency: string;
  expires_at?: string;
  owner: {
    name: string;
    iata_code: string;
    logo_symbol_url?: string;
  };
  slices: Array<{
    id: string;
    origin: {
      iata_code: string;
      city_name: string;
      name?: string;
    };
    destination: {
      iata_code: string;
      city_name: string;
      name?: string;
    };
    departure_time: string;
    arrival_time: string;
    duration: string;
    segments: Array<{
      id: string;
      origin: {
        iata_code: string;
        name?: string;
        city_name?: string;
      };
      destination: {
        iata_code: string;
        name?: string;
        city_name?: string;
      };
      departing_at: string;
      arriving_at: string;
      origin_terminal: string | null;
      destination_terminal: string | null;
      marketing_carrier: {
        name: string;
        iata_code: string;
      };
      marketing_carrier_flight_number: string;
      operating_carrier?: {
        name: string;
        iata_code: string;
      };
      operating_carrier_flight_number?: string;
      duration?: string;
      aircraft: {
        name: string;
        iata_code: string;
      };
      passengers?: Array<{
        id: string;
        cabin_class_marketing_name?: string;
        cabin_class?: string;
      }>;
      stops?: Array<{
        id: string;
        duration: string;
        departing_at: string;
        airport: {
          id: string;
          name: string;
          iata_code: string;
          icao_code: string;
          city_name: string;
          iata_country_code: string;
          longitude: number;
          latitude: number;
          time_zone: string;
          city: {
            id: string;
            name: string;
            iata_code: string;
            iata_country_code: string;
          };
        };
      }>;
    }>;
    conditions?: {
      change_before_departure?: {
        allowed: boolean;
        penalty_amount: string | null;
        penalty_currency: string | null;
      } | null;
      refund_before_departure?: {
        allowed: boolean;
        penalty_amount: string | null;
        penalty_currency: string | null;
      } | null;
    };
  }>;
  conditions?: {
    change_before_departure?: {
      allowed: boolean;
      penalty_amount: string | null;
      penalty_currency: string | null;
    } | null;
    refund_before_departure?: {
      allowed: boolean;
      penalty_amount: string | null;
      penalty_currency: string | null;
    } | null;
  };
  private_fares?: Array<{
    type: 'corporate' | 'leisure';
    corporate_code?: string;
    tracking_reference?: string;
  }>;
  payment_requirements?: {
    requires_instant_payment: boolean;
    price_guarantee_expires_at: string | null;
    payment_required_by: string | null;
  };
  available_actions?: {
    update_passenger?: boolean;
    cancel?: boolean;
    change?: boolean;
  };
  available_services?: DuffelService[];
  intended_payment_methods?: Array<{
    type: 'card' | 'balance';
    card_id?: string;
    surcharge_amount?: string;
    surcharge_currency?: string;
  }>;
  passengers?: Array<{
    id: string;
    type: 'adult' | 'child' | 'infant_without_seat';
    total_amount: string;
    total_currency: string;
  }>;
}

export interface OrderCancellation {
  id: string;
  order_id: string;
  refund_amount: string;
  refund_currency: string;
  refund_to: 'original_form_of_payment' | 'airline_credits';
  expires_at: string;
  confirmed_at: string | null;
  airline_credits?: Array<{
    passenger_id: string;
    credit_name: string;
    credit_currency: string;
    credit_amount: string;
    credit_code: string | null;
  }>;
}

export interface OrderChangeSlice {
  origin: string;
  destination: string;
  departure_date: string;
  cabin_class?: 'economy' | 'premium_economy' | 'business' | 'first';
}

export interface OrderChangeSegment {
  id: string;
  origin: {
    iata_code: string;
    name: string;
    city_name: string;
  };
  destination: {
    iata_code: string;
    name: string;
    city_name: string;
  };
  departing_at: string;
  arriving_at: string;
  duration: string;
  marketing_carrier: {
    name: string;
    iata_code: string;
  };
  marketing_carrier_flight_number: string;
  aircraft: {
    name: string;
    iata_code: string;
  };
}

export interface OrderChangeSliceInfo {
  id: string;
  origin: {
    iata_code: string;
    name: string;
  };
  destination: {
    iata_code: string;
    name: string;
  };
  departing_at?: string;
  arriving_at?: string;
  duration: string;
  segments: OrderChangeSegment[];
}

export interface OrderChangeOffer {
  id: string;
  created_at: string;
  expires_at: string;
  change_total_amount: string;
  change_total_currency: string;
  new_total_amount: string;
  new_total_currency: string;
  penalty_total_amount: string;
  penalty_total_currency: string;
  refund_to: 'original_form_of_payment' | 'voucher' | 'airline_credits';
  live_mode: boolean;
  slices: {
    add: OrderChangeSliceInfo[];
    remove: OrderChangeSliceInfo[];
  };
}

export interface OrderChangeRequest {
  id: string;
  order_id: string;
  slices: {
    remove: Array<{ slice_id: string }>;
    add: OrderChangeSlice[];
  };
  order_change_offers: OrderChangeOffer[];
  created_at: string;
  updated_at: string;
  live_mode: boolean;
}

export interface DuffelService {
  id: string;
  type: 'baggage' | 'seat' | 'meal';
  total_amount: string;
  total_currency: string;
  maximum_quantity: number;
  metadata?: any;
}

export interface SeatMap {
  id: string;
  segment_id: string;
  slice_id: string;
  cabins: Array<{
    name: string;
    aisles: number;
    cabin_class: string;
    deck: number;
    wings?: {
      first_row_index: number;
      last_row_index: number;
    };
    rows: Array<{
      sections: Array<{
        elements: Array<{
          type: 'seat' | 'empty' | 'aisle' | 'exit_row' | 'lavatory' | 'galley';
          available_services?: Array<{
            id: string;
            passenger_id: string;
            total_amount: string;
            total_currency: string;
          }>;
          designator?: string;
          name?: string;
          disclosures?: string[];
        }>;
      }>;
    }>;
  }>;
}

export interface LoyaltyProgrammeAccount {
  airline_iata_code: string;
  account_number: string;
}

export interface OfferWithCreditInfo extends DuffelOffer {
  canUseCredits: boolean;
  compatibleCreditIds: string[];
  // [TAXAS] Campos adicionais para preços com taxas
  finalPricePerPassengerAOA?: number;
  feeBreakdown?: {
    basePriceAOA: number;
    exchangeRate: number;
    percentageFee: number;
    fixedFee: number;
    totalFees: number;
    finalPrice: number;
  };
}

export interface OrderPassenger {
  id: string;
  title: string;
  given_name: string;
  family_name: string;
  gender: 'm' | 'f';
  born_on: string;
  email: string;
  phone_number: string;
  infant_passenger_id?: string;
  loyalty_programme_accounts?: LoyaltyProgrammeAccount[];
}

export interface UpdateOrderPassengerData {
  title?: string;
  given_name?: string;
  family_name?: string;
  gender?: string;
  born_on?: string;
  email?: string;
  phone_number?: string;
  loyalty_programme_accounts?: LoyaltyProgrammeAccount[];
}

// ============================================================================
// [TAXAS] CONFIGURAÇÃO DAS TAXAS DA EMPRESA (NOVO)
// ============================================================================

const ANGOLA_AIRPORTS = [
  'LAD', 'NBJ', 'CAB', 'SDD', 'NOV', 'SVP', 'MEG', 'VPE', 
  'SSY', 'LUO', 'CBT', 'BUG', 'SZA', 'SPP', 'MSZ', 'VHC'
];

const COMPANY_FEES = {
  FIXED_FEE: 5000, // Taxa fixa por passageiro (5.000 Kz)
  PERCENTAGE_FEE: 0.15, // Taxa percentual padrão de 15%
  MINIMUM_FEE: 2000, // Taxa mínima (2.000 Kz)
  MAXIMUM_FEE: 5000000, // Taxa máxima (5.000.000 Kz)
  ENABLED: true, // Habilitar/desabilitar taxas
  FEE_DESCRIPTION: 'Taxas incluem processamento, seguro e serviços DG-Travel África',
};

// [TAXAS] Taxas de câmbio (serão atualizadas automaticamente)
let EXCHANGE_RATES = {
  USD: parseFloat(import.meta.env.VITE_EXCHANGE_RATE_USD_AOA || '1350'),
  EUR: parseFloat(import.meta.env.VITE_EXCHANGE_RATE_EUR_AOA || '1475'),
  GBP: 1624, // Fallback
  lastUpdated: new Date().toISOString(),
};

// [TAXAS] Função para DEFINIR/CUSTOMIZAR taxas de câmbio (chamado pelo admin)
export const setCustomExchangeRates = async (
  usdRate: number,
  eurRate: number,
  gbpRate: number
): Promise<void> => {
  try {
    console.log('🔄 Atualizando taxas de câmbio customizadas...');

    const { supabase } = await import('./supabaseClient');

    const { error } = await supabase
      .from('site_settings')
      .upsert([
        { setting_key: 'exchange_rate_usd_aoa', setting_value: String(usdRate), setting_type: 'currency' },
        { setting_key: 'exchange_rate_eur_aoa', setting_value: String(eurRate), setting_type: 'currency' },
        { setting_key: 'exchange_rate_gbp_aoa', setting_value: String(gbpRate), setting_type: 'currency' },
      ], { onConflict: 'setting_key' });

    if (error) {
      console.error('❌ Erro ao salvar taxas customizadas no banco:', error);
    }

    EXCHANGE_RATES = {
      USD: usdRate,
      EUR: eurRate,
      GBP: gbpRate,
      lastUpdated: new Date().toISOString(),
    };

    console.log('✅ Taxas de câmbio customizadas:', EXCHANGE_RATES);
  } catch (error) {
    console.error('❌ Erro ao definir taxas customizadas:', error);
  }
};

// [TAXAS] Função para DEFINIR/CUSTOMIZAR taxas da empresa (chamado pelo admin)
export const setCustomCompanyFees = async (
  percentageFee: number,
  fixedFee: number = 5000
): Promise<void> => {
  try {
    console.log('🔄 Atualizando taxas da empresa customizadas...');

    const { supabase } = await import('./supabaseClient');

    const { error } = await supabase
      .from('site_settings')
      .upsert([
        { setting_key: 'company_percentage_fee', setting_value: String(percentageFee), setting_type: 'fee' },
        { setting_key: 'company_fixed_fee', setting_value: String(fixedFee), setting_type: 'fee' },
      ], { onConflict: 'setting_key' });

    if (error) {
      console.error('❌ Erro ao salvar taxas da empresa no banco:', error);
    }

    COMPANY_FEES.PERCENTAGE_FEE = percentageFee;
    COMPANY_FEES.FIXED_FEE = fixedFee;

    console.log('✅ Taxas da empresa atualizadas:', { percentageFee, fixedFee });
  } catch (error) {
    console.error('❌ Erro ao definir taxas da empresa:', error);
  }
};

// [TAXAS] Função para CARREGAR taxas de câmbio e taxas da empresa do banco de dados (Supabase)
export const loadExchangeRatesFromDB = async (): Promise<void> => {
  try {
    console.log('🔄 Carregando taxas do banco de dados...');

    const { supabase } = await import('./supabaseClient');

    const { data, error } = await supabase
      .from('site_settings')
      .select('setting_key, setting_value')
      .in('setting_key', [
        'exchange_rate_usd_aoa', 
        'exchange_rate_eur_aoa', 
        'exchange_rate_gbp_aoa',
        'company_percentage_fee',
        'company_fixed_fee'
      ]);

    if (error) {
      console.error('❌ Erro ao carregar taxas do banco:', error);
      return;
    }

    if (data && data.length > 0) {
      data.forEach(item => {
        if (item.setting_value && parseFloat(item.setting_value) >= 0) {
          if (item.setting_key === 'exchange_rate_usd_aoa') {
            EXCHANGE_RATES.USD = parseFloat(item.setting_value);
          }
          if (item.setting_key === 'exchange_rate_eur_aoa') {
            EXCHANGE_RATES.EUR = parseFloat(item.setting_value);
          }
          if (item.setting_key === 'exchange_rate_gbp_aoa') {
            EXCHANGE_RATES.GBP = parseFloat(item.setting_value);
          }
          if (item.setting_key === 'company_percentage_fee') {
            COMPANY_FEES.PERCENTAGE_FEE = parseFloat(item.setting_value);
          }
          if (item.setting_key === 'company_fixed_fee') {
            COMPANY_FEES.FIXED_FEE = parseFloat(item.setting_value);
          }
        }
      });
      EXCHANGE_RATES.lastUpdated = new Date().toISOString();
      console.log('✅ Taxas carregadas do banco:', { EXCHANGE_RATES, COMPANY_FEES });
    }
  } catch (error) {
    console.error('❌ Erro ao carregar taxas do banco:', error);
  }
};

// [TAXAS] Função para buscar taxas de câmbio atualizadas
export const fetchExchangeRates = async (): Promise<void> => {
  try {
    console.log('🔄 Atualizando taxas de câmbio...');

    // Carregar do banco de dados (prioridade máxima)
    await loadExchangeRatesFromDB();

    // Se as taxas forem as padrões (1250, 1375, 1624), podemos tentar atualizar da API
    const isDefault = EXCHANGE_RATES.USD === 1250 && EXCHANGE_RATES.EUR === 1375 && EXCHANGE_RATES.GBP === 1624;

    if (isDefault) {
      console.log('ℹ️ Usando taxas padrões. Tentando atualizar via API...');
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();

      if (data.rates && data.rates.AOA) {
        // Só atualizamos se as taxas da API forem razoáveis (ex: > 500)
        if (data.rates.AOA > 500) {
          EXCHANGE_RATES = {
            USD: data.rates.AOA,
            EUR: data.rates.AOA / data.rates.EUR,
            GBP: data.rates.AOA / data.rates.GBP,
            lastUpdated: new Date().toISOString(),
          };
          console.log('✅ Taxas de câmbio atualizadas via API:', EXCHANGE_RATES);
        }
      }
    } else {
      console.log('✅ Mantendo taxas customizadas do sistema:', EXCHANGE_RATES);
    }
  } catch (error) {
    console.error('❌ Erro ao buscar taxas de câmbio:', error);
  }
};

// [TAXAS] Função para calcular taxas da empresa
export const calculateCompanyFees = (
  amountInAOA: number,
  originIATA?: string,
  destinationIATA?: string
): {
  percentageAmount: number;
  fixedAmount: number;
  totalFees: number;
  finalAmount: number;
  currentPercentage: number;
} => {
  if (!COMPANY_FEES.ENABLED) {
    return {
      percentageAmount: 0,
      fixedAmount: 0,
      totalFees: 0,
      finalAmount: amountInAOA,
      currentPercentage: 0,
    };
  }

  // Lógica de Taxas Dinâmicas baseada em Origem/Destino (Sincronizada com API Real)
  // Usar a taxa definida nas configurações (COMPANY_FEES.PERCENTAGE_FEE)
  const percentageFee = COMPANY_FEES.PERCENTAGE_FEE;

  /* 
  // Lógica anterior comentada para garantir que os 15% sejam aplicados globalmente sem exceção
  if (originIATA) {
    ...
  }
  */

  const percentageAmount = amountInAOA * percentageFee;
  const fixedAmount = COMPANY_FEES.FIXED_FEE;
  let totalFees = percentageAmount + fixedAmount;
  
  totalFees = Math.max(totalFees, COMPANY_FEES.MINIMUM_FEE);
  totalFees = Math.min(totalFees, COMPANY_FEES.MAXIMUM_FEE);
  
  return {
    percentageAmount,
    fixedAmount,
    totalFees,
    finalAmount: amountInAOA + totalFees,
    currentPercentage: percentageFee,
  };
};

// [TAXAS] Função para obter taxa de câmbio
export const getExchangeRate = (fromCurrency: string = 'EUR'): number => {
  const currency = fromCurrency.toUpperCase();
  // Se for AOA, a taxa é 1
  if (currency === 'AOA') return 1;
  
  if (currency === 'USD') return EXCHANGE_RATES.USD;
  if (currency === 'EUR') return EXCHANGE_RATES.EUR;
  if (currency === 'GBP') return EXCHANGE_RATES.GBP;
  
  // Fallback para EUR
  return EXCHANGE_RATES.EUR;
};

// [TAXAS] Função para obter detalhamento completo do preço
export const getPriceBreakdown = (
  usdAmount: number,
  fromCurrency: string = 'EUR',
  originIATA?: string,
  destinationIATA?: string
): {
  originalAmount: number;
  originalCurrency: string;
  exchangeRate: number;
  convertedAmount: number;
  percentageFee: number;
  fixedFee: number;
  totalFees: number;
  finalAmount: number;
  formattedOriginal: string;
  formattedFinal: string;
  currentPercentage: number;
} => {
  const exchangeRate = getExchangeRate(fromCurrency);
  const convertedAmount = usdAmount * exchangeRate;
  const fees = calculateCompanyFees(convertedAmount, originIATA, destinationIATA);
  
  return {
    originalAmount: usdAmount,
    originalCurrency: fromCurrency,
    exchangeRate,
    convertedAmount,
    percentageFee: fees.percentageAmount,
    fixedFee: fees.fixedAmount,
    totalFees: fees.totalFees,
    finalAmount: fees.finalAmount,
    formattedOriginal: `${usdAmount.toFixed(2)} ${fromCurrency}`,
    formattedFinal: formatAOA(fees.finalAmount),
    currentPercentage: fees.currentPercentage,
  };
};

// [TAXAS] Função CENTRALIZADA para calcular preços de voos (Sincronizada)
export const calculateFlightPrices = (
  totalAmountEUR: number,
  currency: string = 'EUR',
  passengerCounts: { adults: number; children: number; infants: number },
  offer?: DuffelOffer
): {
  adultPriceAOA: number;
  childPriceAOA: number;
  infantPriceAOA: number;
  totalAOA: number;
} => {
  const exchangeRate = getExchangeRate(currency);
  
  let adultBaseAOA = 0;
  let childBaseAOA = 0;
  let infantBaseAOA = 0;

  // 1. Tentar obter preços REAIS por tipo de passageiro da oferta da Duffel
  if (offer && offer.passengers && offer.passengers.length > 0) {
    const adultPax = offer.passengers.find(p => p.type === 'adult');
    const childPax = offer.passengers.find(p => p.type === 'child');
    const infantPax = offer.passengers.find(p => p.type === 'infant_without_seat');

    if (adultPax) adultBaseAOA = parseFloat(adultPax.total_amount) * exchangeRate;
    if (childPax) childBaseAOA = parseFloat(childPax.total_amount) * exchangeRate;
    if (infantPax) infantBaseAOA = parseFloat(infantPax.total_amount) * exchangeRate;
  }

  // 2. Fallback para lógica ponderada se os preços por passageiro não estiverem disponíveis ou incompletos
  if (!adultBaseAOA) {
    const weightedPassengerCount = 
      (passengerCounts.adults * 1.0) + 
      (passengerCounts.children * 0.9) + 
      (passengerCounts.infants * 0.15);
    
    const totalBaseAOA = totalAmountEUR * exchangeRate;
    adultBaseAOA = totalBaseAOA / (weightedPassengerCount || 1);
    
    if (!childBaseAOA) childBaseAOA = adultBaseAOA * 0.9;
    if (!infantBaseAOA) infantBaseAOA = adultBaseAOA * 0.15;
  } else {
    // Garantir que temos preços para todos os tipos que estão na contagem
    if (passengerCounts.children > 0 && !childBaseAOA) childBaseAOA = adultBaseAOA * 0.9;
    if (passengerCounts.infants > 0 && !infantBaseAOA) infantBaseAOA = adultBaseAOA * 0.15;
  }
  
  // 3. Aplicar taxas da empresa INDIVIDUALMENTE para cada passageiro
  // Garantir que a taxa de 15% seja aplicada
  const origin = offer?.slices?.[0]?.origin?.iata_code;
  const destination = offer?.slices?.[0]?.destination?.iata_code;

  const adultWithFees = calculateCompanyFees(adultBaseAOA, origin, destination).finalAmount;
  const childWithFees = childBaseAOA > 0 ? calculateCompanyFees(childBaseAOA, origin, destination).finalAmount : 0;
  const infantWithFees = infantBaseAOA > 0 ? calculateCompanyFees(infantBaseAOA, origin, destination).finalAmount : 0;
  
  // 4. Calcular o total final somando os passageiros individuais
  const finalTotalAOA = 
    (Math.round(adultWithFees) * passengerCounts.adults) + 
    (Math.round(childWithFees) * passengerCounts.children) + 
    (Math.round(infantWithFees) * passengerCounts.infants);
    
  return {
    adultPriceAOA: Math.round(adultWithFees),
    childPriceAOA: Math.round(childWithFees),
    infantPriceAOA: Math.round(infantWithFees),
    totalAOA: finalTotalAOA
  };
};

export type FlightOffer = DuffelOffer;

/**
 * Buscar ofertas de voos existentes (Mock ou API)
 */
export const getFlightOffers = async (): Promise<FlightOffer[]> => {
  // Por agora retornamos um array vazio ou implementamos a lógica real se necessário
  // Geralmente usado no painel admin para listar ofertas recentes
  return [];
};

export const formatAOA = (amount: number): string => {
  return `${amount.toLocaleString('pt-AO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Kz`;
};

// [TAXAS] FUNÇÃO MODIFICADA - Agora inclui taxas da empresa
export const convertUSDtoAOA = (
  usdAmount: number,
  fromCurrency: string = 'EUR',
  includeFees: boolean = true,
  originIATA?: string,
  destinationIATA?: string
): number => {
  const exchangeRate = getExchangeRate(fromCurrency);
  const amountInAOA = usdAmount * exchangeRate;
  
  if (includeFees) {
    const fees = calculateCompanyFees(amountInAOA, originIATA, destinationIATA);
    console.log('💰 Preço com taxas:', {
      original: usdAmount,
      converted: amountInAOA,
      fees: fees.totalFees,
      final: fees.finalAmount,
      origin: originIATA,
      destination: destinationIATA,
      percentage: fees.currentPercentage
    });
    return fees.finalAmount;
  }
  
  return amountInAOA;
};

export const formatDuration = (duration: string): string => {
  const match = duration.match(/PT(\d+H)?(\d+M)?/);
  if (!match) return duration;

  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;

  if (hours && minutes) {
    return `${hours}h ${minutes}m`;
  } else if (hours) {
    return `${hours}h`;
  } else if (minutes) {
    return `${minutes}m`;
  }
  return duration;
};

export const formatDateTime = (dateTimeString: string): { date: string; time: string } => {
  const date = new Date(dateTimeString);
  
  const timeString = date.toLocaleTimeString('pt-PT', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const dateString = date.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return {
    time: timeString,
    date: dateString,
  };
};

export const getConditionsText = (offer: DuffelOffer): string => {
  return 'Condições aplicáveis';
};

export const getCreditBadgeInfo = (offer: OfferWithCreditInfo): {
  show: boolean;
  text: string;
  color: string;
  icon: string;
} => {
  if (offer.canUseCredits && offer.compatibleCreditIds.length > 0) {
    return {
      show: true,
      text: 'Aceita Créditos',
      color: 'bg-green-100 text-green-800',
      icon: 'ri-check-line',
    };
  }
  return {
    show: false,
    text: '',
    color: '',
    icon: '',
  };
};

export const sortOffersByCreditsCompatibility = (
  offers: OfferWithCreditInfo[],
  hasSelectedCredits: boolean
): OfferWithCreditInfo[] => {
  if (!hasSelectedCredits) {
    return offers;
  }

  return [...offers].sort((a, b) => {
    if (a.canUseCredits && !b.canUseCredits) return -1;
    if (!a.canUseCredits && b.canUseCredits) return 1;
    return 0;
  });
};

export const filterOffersByCredits = (
  offers: OfferWithCreditInfo[],
  selectedCreditIds: string[]
): OfferWithCreditInfo[] => {
  if (selectedCreditIds.length === 0) {
    return offers;
  }
  return offers.filter(offer => offer.canUseCredits);
};

// -------------------------------------------------------------------------
// API Functions
// -------------------------------------------------------------------------

import { supabase } from './supabaseClient';

export const searchFlights = async (
  origin: string,
  destination: string,
  departureDate: string,
  returnDate?: string,
  passengers: number | Array<{ type: string; age?: number }> = 1,
  cabinClass: 'economy' | 'premium_economy' | 'business' | 'first' = 'economy',
  maxConnections?: number,
  privateFares?: any,
  selectedAirlines?: string[]
): Promise<OfferWithCreditInfo[]> => {
  try {
    const passengersPayload = typeof passengers === 'number' 
      ? Array(passengers).fill({ type: 'adult' }) 
      : passengers;

    const { data, error: invokeError } = await supabase.functions.invoke('duffel-search-flights', {
      body: {
        origin,
        destination,
        departureDate,
        returnDate,
        passengers: passengersPayload,
        cabinClass,
        maxConnections,
        privateFares,
        selectedAirlines,
      },
    });

    // Se houver erro no invoke (ex: 400, 422, 500 da Edge Function ou erro de rede)
    if (invokeError) {
      console.error('❌ Invoke error details:', invokeError);
      
      // Capturar erro 500 para redirecionar
      if (invokeError.status === 500 || invokeError.message?.includes('500')) {
        window.location.href = '/erro';
        return [];
      }

      // @ts-ignore
      const bodyError = invokeError.context?.json?.error || invokeError.message;
      throw new Error(bodyError || 'Erro ao pesquisar voos');
    }

    // A Edge Function agora pode retornar 200 com { error: "..." } para evitar avisos no console
    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error, data.details || '');
      throw new Error(data.error);
    }

    // Log robusto para depuração
    console.log('📥 Resultados da pesquisa:', {
      hasData: !!data,
      offersCount: data?.data?.offers?.length || data?.offers?.length || (Array.isArray(data) ? data.length : 0),
      firstOfferCurrency: data?.data?.offers?.[0]?.total_currency || 'N/A'
    });

    // Normalização da resposta da Duffel
    let offers: DuffelOffer[] = [];
    
    if (data && data.data && data.data.offers && Array.isArray(data.data.offers)) {
      offers = data.data.offers;
    } else if (data && data.offers && Array.isArray(data.offers)) {
      offers = data.offers;
    } else if (data && Array.isArray(data)) {
      offers = data;
    }

    const passengersArray = Array.isArray(passengersPayload) ? passengersPayload : [];
    const passengerCounts = {
      adults: passengersArray.filter(p => !p.type || p.type === 'adult').length,
      children: passengersArray.filter(p => p.type === 'child').length,
      infants: passengersArray.filter(p => p.type === 'infant_without_seat' || p.type === 'infant').length,
    };

    // [TAXAS] Adicionar informações de preços com taxas sincronizadas
    return (offers as any[]).map((offer: DuffelOffer) => {
      const baseAmount = parseFloat(offer.total_amount || '0');
      const currency = offer.total_currency || 'USD';
      
      const pricing = calculateFlightPrices(baseAmount, currency, passengerCounts, offer);
      const origin = offer.slices?.[0]?.origin?.iata_code;
      const destination = offer.slices?.[0]?.destination?.iata_code;
      const breakdown = getPriceBreakdown(baseAmount, currency, origin, destination);
      
      return {
        ...offer,
        canUseCredits: false,
        compatibleCreditIds: [],
        // [TAXAS] Campos adicionais sincronizados
        finalPricePerPassengerAOA: pricing.adultPriceAOA,
        feeBreakdown: {
          basePriceAOA: breakdown.convertedAmount,
          exchangeRate: breakdown.exchangeRate,
          percentageFee: breakdown.percentageFee,
          fixedFee: breakdown.fixedFee,
          totalFees: breakdown.totalFees,
          finalPrice: pricing.totalAOA,
          currentPercentage: breakdown.currentPercentage,
        }
      };
    });

  } catch (error: any) {
    console.error('Error searching flights:', error);
    // Se o erro for do Supabase (status code não 2xx), ele vem formatado
    const message = error.context?.json?.error || error.message || 'Erro inesperado na pesquisa de voos';
    throw new Error(message);
  }
};


export const searchFlightsWithCredits = async (params: {
  origin: string;
  destination: string;
  departureDate: string;
  returnDate?: string;
  passengers: Array<{ 
    type?: string; 
    age?: number; 
    given_name?: string; 
    family_name?: string; 
    fare_type?: string;
    loyalty_programme_accounts?: LoyaltyProgrammeAccount[];
  }>;
  cabinClass: 'economy' | 'premium_economy' | 'business' | 'first';
  maxConnections?: number;
  privateFares?: any;
  userId?: string;
  airlineCreditIds?: string[];
  selectedAirlines?: string[];
}): Promise<OfferWithCreditInfo[]> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-search-flights', {
      body: {
        origin: params.origin,
        destination: params.destination,
        departureDate: params.departureDate,
        returnDate: params.returnDate,
        passengers: params.passengers,
        cabinClass: params.cabinClass,
        maxConnections: params.maxConnections,
        privateFares: params.privateFares,
        userId: params.userId,
        airlineCreditIds: params.airlineCreditIds,
        selectedAirlines: params.selectedAirlines,
      },
    });

    if (invokeError) {
      console.error('Invoke error:', invokeError);
      
      // Capturar erro 500 para redirecionar
      if (invokeError.status === 500 || invokeError.message?.includes('500')) {
        window.location.href = '/erro';
        return [];
      }

      const errorMsg = invokeError.context?.json?.error || invokeError.message || 'Erro ao pesquisar voos com créditos';
      throw new Error(errorMsg);
    }

    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error, data.details || '');
      throw new Error(data.error);
    }

    console.log('📥 Resposta da pesquisa com créditos:', {
      hasData: !!data,
      offersCount: data?.data?.offers?.length || data?.offers?.length || (Array.isArray(data) ? data.length : 0)
    });

    let offers: DuffelOffer[] = [];
    
    if (data && data.data && data.data.offers && Array.isArray(data.data.offers)) {
      offers = data.data.offers;
    } else if (data && data.offers && Array.isArray(data.offers)) {
      offers = data.offers;
    }

    const passengerCounts = {
      adults: params.passengers.filter(p => !p.type || p.type === 'adult').length,
      children: params.passengers.filter(p => p.type === 'child').length,
      infants: params.passengers.filter(p => p.type === 'infant_without_seat').length,
    };

    // [TAXAS] Adicionar informações de preços com taxas
    return (offers as any[]).map((offer: DuffelOffer) => {
      const baseAmount = parseFloat(offer.total_amount || '0');
      const currency = offer.total_currency || 'USD';
      
      const pricing = calculateFlightPrices(baseAmount, currency, passengerCounts, offer);
      const origin = offer.slices[0]?.origin.iata_code;
      const destination = offer.slices[0]?.destination.iata_code;
      const breakdown = getPriceBreakdown(baseAmount, currency, origin, destination);
      
      return {
        ...offer,
        canUseCredits: checkIfOfferAcceptsCredits(offer, params.airlineCreditIds),
        compatibleCreditIds: getCompatibleCreditIds(offer, params.airlineCreditIds),
        // [TAXAS] Campos adicionais sincronizados
        finalPricePerPassengerAOA: pricing.adultPriceAOA,
        feeBreakdown: {
          basePriceAOA: breakdown.convertedAmount,
          exchangeRate: breakdown.exchangeRate,
          percentageFee: breakdown.percentageFee,
          fixedFee: breakdown.fixedFee,
          totalFees: breakdown.totalFees,
          finalPrice: pricing.totalAOA, // Usamos o total calculado corretamente
          currentPercentage: breakdown.currentPercentage,
        }
      };
    });
  } catch (error: any) {
    console.error('Error searching flights with credits:', error);
    throw error;
  }
};

export const getOfferById = async (offerId: string, returnAvailableServices: boolean = false): Promise<{ data: DuffelOffer }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-get-offer', {
      body: { offerId, returnAvailableServices },
    });

    if (invokeError) throw invokeError;
    
    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error, data.details || '');
      throw new Error(data.error);
    }

    if (data && data.data) {
      return { data: data.data };
    }
    
    if (data && data.offer) {
      return { data: data.offer };
    }

    return { data: data };
  } catch (error: any) {
    console.error('Error getting offer:', error);
    throw error;
  }
};

export const getSeatMaps = async (offerId: string): Promise<{ data: SeatMap[] }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-get-seat-maps', {
      body: { offerId },
    });

    if (invokeError) throw invokeError;

    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error, data.details || '');
      throw new Error(data.error);
    }

    if (data && data.data) {
      return { data: data.data };
    }

    return { data: data };
  } catch (error: any) {
    console.error('Error getting seat maps:', error);
    throw error;
  }
};

export const createOrder = async (orderData: any): Promise<{ data: any }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-create-order', {
      body: orderData,
    });

    if (invokeError) {
      console.error('❌ Invoke error details:', invokeError);
      // @ts-ignore
      const bodyError = invokeError.context?.json?.error || invokeError.message;
      throw new Error(bodyError || 'Erro ao criar reserva');
    }

    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error);
      throw new Error(data.error);
    }

    if (data && data.data) {
      return { data: data.data };
    }

    return { data };
  } catch (error: any) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const priceOffer = async (
  offerId: string,
  intendedPaymentMethods: Array<{ type: 'card' | 'balance'; card_id?: string }>,
  intendedServices: Array<{ quantity: number; id: string }>
): Promise<{ data: DuffelOffer }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-price-offer', {
      body: { offerId, intendedPaymentMethods, intendedServices },
    });

    if (invokeError) throw invokeError;
    
    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error);
      throw new Error(data.error);
    }

    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error pricing offer:', error);
    throw error;
  }
};

export const priceOrder = async (
  orderId: string,
  intendedPaymentMethods: Array<{ type: 'card' | 'balance'; card_id?: string }>
): Promise<{ data: any }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-price-order', {
      body: { orderId, intendedPaymentMethods },
    });

    if (invokeError) throw invokeError;
    
    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error);
      throw new Error(data.error);
    }

    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error pricing order:', error);
    throw error;
  }
};

export const completeOrderPayment = async (
  orderId: string,
  payments: Array<{
    three_d_secure_session_id?: string;
    currency: string;
    amount: string;
    type?: 'card' | 'balance';
  }>
): Promise<{ data: any }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-complete-order-payment', {
      body: { orderId, payments },
    });

    if (invokeError) {
      console.error('Invoke error:', invokeError);
      const errorMsg = invokeError.context?.json?.error || invokeError.message || 'Erro ao completar pagamento';
      throw new Error(errorMsg);
    }

    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error);
      throw new Error(data.error);
    }

    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error completing order payment:', error);
    throw error;
  }
};

export const getOrder = async (orderId: string): Promise<{ data: any }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke('duffel-get-order', {
      body: { orderId },
    });

    if (invokeError) {
      console.error('Invoke error:', invokeError);
      const errorMsg = invokeError.context?.json?.error || invokeError.message || 'Erro ao obter pedido';
      throw new Error(errorMsg);
    }

    if (data && data.error) {
      console.error('❌ Edge Function error:', data.error);
      throw new Error(data.error);
    }

    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error getting order:', error);
    throw error;
  }
};

export const createPayment = async (orderId: string, amount: string, currency: string): Promise<{ data: any }> => {
  try {
    const { data, error } = await supabase.functions.invoke('duffel-create-payment', {
      body: { orderId, amount, currency },
    });

    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Error creating payment:', error);
    throw error;
  }
};

export const createCancellationQuote = async (orderId: string): Promise<{ data: OrderCancellation }> => {
  try {
    const { data, error } = await supabase.functions.invoke('duffel-create-cancellation-quote', {
      body: { orderId },
    });

    if (error) throw error;
    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error creating cancellation quote:', error);
    throw error;
  }
};

export const confirmCancellation = async (orderCancellationId: string): Promise<{ data: OrderCancellation }> => {
  try {
    const { data, error } = await supabase.functions.invoke('duffel-confirm-cancellation', {
      body: { orderCancellationId },
    });

    if (error) throw error;
    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error confirming cancellation:', error);
    throw error;
  }
};

export const createOrderChangeRequest = async (
  orderId: string,
  slicesToRemove: Array<{ slice_id: string }>,
  slicesToAdd: OrderChangeSlice[]
): Promise<{ data: OrderChangeRequest }> => {
  try {
    const { data, error } = await supabase.functions.invoke('duffel-create-order-change-request', {
      body: { orderId, slicesToRemove, slicesToAdd },
    });

    if (error) throw error;
    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error creating order change request:', error);
    throw error;
  }
};

export const confirmOrderChange = async (
  orderChangeOfferId: string,
  payment?: {
    type: 'balance' | 'arc_bsp_cash';
    amount: string;
    currency: string;
  }
): Promise<{ data: any }> => {
  try {
    const { data, error } = await supabase.functions.invoke('duffel-confirm-order-change', {
      body: { 
        order_change_offer_id: orderChangeOfferId, 
        payment 
      },
    });

    if (error) throw error;
    return { data: data.data || data };
  } catch (error: any) {
    console.error('Error confirming order change:', error);
    throw error;
  }
};

export const updateOrderPassenger = async (
  orderId: string,
  passengerId: string,
  passengerData: UpdateOrderPassengerData
): Promise<{ data: any }> => {
  try {
    const { data, error } = await supabase.functions.invoke('duffel-update-order-passenger', {
      body: {
        orderId,
        passengerId,
        passengerData,
      },
    });

    if (error) throw error;

    if (data && data.data) {
      return { data: data.data };
    }

    return { data };
  } catch (error: any) {
    console.error('Error updating order passenger:', error);
    throw error;
  }
};

export const findAirportsByLocation = async (
  lat: number,
  lng: number,
  rad: number = 100000
): Promise<any> => {
  try {
    const { data, error } = await supabase.functions.invoke('duffel-airports-by-location', {
      body: { lat, lng, rad },
    });

    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Error finding airports by location:', error);
    throw error;
  }
};


// ============================================================================
// [TAXAS] FUNÇÕES AUXILIARES
// ============================================================================

const checkIfOfferAcceptsCredits = (offer: DuffelOffer, creditIds: string[]): boolean => {
  return creditIds.length > 0;
};

const getCompatibleCreditIds = (offer: DuffelOffer, creditIds: string[]): string[] => {
  return creditIds;
};

// ============================================================================
// [TAXAS] INICIALIZAÇÃO
// ============================================================================

// Buscar taxas de câmbio ao iniciar
fetchExchangeRates();

// Atualizar taxas a cada 1 hora
setInterval(fetchExchangeRates, 3600000);

// Exportar configurações para uso em outros arquivos
export { COMPANY_FEES, EXCHANGE_RATES };
export { 
  setCustomExchangeRates as updateCustomExchangeRates, 
  setCustomCompanyFees as updateCustomCompanyFees,
  loadExchangeRatesFromDB as syncExchangeRatesFromDB 
};